import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Chip de atención: el interruptor-píldora que filtra "lo que pide mirar hoy"
 * (Atrasados, Novedades, Dudas…). Lleva un punto de color, el rótulo y el
 * contador en mono; encendido sube a su tono soft con borde y texto del color.
 *
 * Es un `button` con `aria-pressed`, no un radio: se pueden apagar todos y el
 * foco lo recorre con Tab como a cualquier botón. Tonos: `danger`, `info` y
 * `warning`, cada uno un par de tokens fondo/borde/texto — nunca un hex.
 */
const punto = {
  danger: "bg-danger-dot",
  info: "bg-info-dot",
  warning: "bg-warning-dot",
}

const chipVariants = cva(
  "inline-flex h-[1.875rem] items-center gap-[0.4375rem] rounded-full border-[1.5px] px-[0.6875rem] text-xs font-bold whitespace-nowrap transition-colors outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  {
    variants: {
      activo: {
        false: "border-border bg-card text-ink-secundario hover:border-ink-ghost",
        true: "",
      },
      tono: {
        danger: "",
        info: "",
        warning: "",
      },
    },
    compoundVariants: [
      { activo: true, tono: "danger", className: "border-danger-texto bg-danger-soft text-danger-texto" },
      { activo: true, tono: "info", className: "border-info-soft-foreground bg-info-soft text-info-soft-foreground" },
      { activo: true, tono: "warning", className: "border-warning-dot bg-warning-soft text-warning-texto" },
    ],
  }
)

function ChipDeAtencion({
  tono,
  activo,
  cantidad,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"button">, "type"> & {
  tono: "danger" | "info" | "warning"
  activo: boolean
  cantidad: number
}) {
  return (
    <button
      type="button"
      data-slot="chip-de-atencion"
      aria-pressed={activo}
      className={cn(chipVariants({ activo, tono }), className)}
      {...props}
    >
      <span aria-hidden className={cn("size-[0.4375rem] rounded-full", punto[tono])} />
      {children}
      <span className="font-mono text-[0.6875rem]">{cantidad}</span>
    </button>
  )
}

export { ChipDeAtencion }
